import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import ModuleSwitch from './ModuleSwitch';
import { 
  LayoutDashboard, 
  Users, 
  UserCheck, 
  Settings, 
  Sun,
  ChevronLeft,
  Home,
  Calculator,
  TrendingUp,
  ShoppingCart,
  Truck,
  Package,
  Calendar,
  GraduationCap,
  Briefcase,
  Euro,
  Lightbulb,
  MessageCircle,
  Trophy,
  Clock,
  UserPlus,
  Zap,
  User,
  Cog,
  StickyNote,
  Shield,
  Menu,
  X,
  FileText
} from 'lucide-react';

const Sidebar = ({ currentModule, userRole, user, onModuleChange, isCollapsed: parentCollapsed, onCollapsedChange, mobileMenuOpen, onMobileMenuToggle }) => {
  const location = useLocation();
  
  // Utiliser l'état contrôlé depuis AppLayout si fourni, sinon état local
  const [localCollapsed, setLocalCollapsed] = React.useState(false);
  const isCollapsed = parentCollapsed !== undefined ? parentCollapsed : localCollapsed;
  const setIsCollapsed = onCollapsedChange || setLocalCollapsed;

  // Déterminer le module à afficher selon le rôle
  const getDisplayModule = () => {
    if (userRole === 'admin') {
      // Admin peut switcher entre modules
      return currentModule || 'vente';
    } else {
      // Autres utilisateurs ont un module fixe selon leur rôle
      if (userRole === 'commercial' || userRole === 'manager_vente' || userRole === 'employe_vente') {
        return 'vente';
      } else if (userRole === 'technicien' || userRole === 'depot' || userRole === 'manager_logistique' || userRole === 'employe_logistique') {
        return 'logistique';
      } else if (userRole === 'comptable' || userRole === 'manager_rh' || userRole === 'employe_rh') {
        return 'rh';
      }
      return 'vente'; // par défaut
    }
  };

  const displayModule = getDisplayModule();

  const getModuleDisplayName = (module) => {
    switch(module) {
      case 'vente': return 'VENTE';
      case 'marketing': return 'MARKETING';
      case 'logistique': return 'OPÉRATIONS';
      case 'rh': return 'RH';
      default: return 'VENTE';
    }
  };

  const getModuleTextColor = (module) => {
    switch(module) {
      case 'vente': return 'text-blue-500';
      case 'marketing': return 'text-pink-500';
      case 'logistique': return 'text-green-500';
      case 'rh': return 'text-purple-500';
      default: return 'text-primary';
    }
  };

  // Menu items par module
  const getMenuItems = () => {
    const baseCommonItems = [
      {
        path: '/',
        icon: LayoutDashboard,
        label: 'Dashboard',
        badge: null
      },
      {
        path: '/commerciaux',
        icon: User,
        label: 'Commerciaux',
        badge: null
      }
    ];

    // Ajouter le module Challenge pour les commerciaux et admins
    const challengeAccessRoles = ['admin', 'commercial', 'manager_vente', 'employe_vente'];
    const challengeItem = challengeAccessRoles.includes(userRole) ? [{
      path: '/challenges',
      icon: Trophy,
      label: 'Challenges',
      badge: userRole === 'commercial' ? 'hot' : null
    }] : [];

    // Modules communs dans l'ordre demandé
    const orderedCommonItems = [
      ...baseCommonItems,
      ...challengeItem,
      {
        path: '/chat',
        icon: MessageCircle,
        label: 'Chat',
        badge: null
      },
      {
        path: '/boite-a-idees',
        icon: Lightbulb,
        label: 'Boîte à idées',
        badge: null
      },
      {
        path: '/bloc-notes',
        icon: StickyNote,
        label: 'Bloc-notes',
        badge: null
      },
      {
        path: '/reglages',
        icon: Cog,
        label: 'Réglages',
        badge: null
      }
    ];

    switch(displayModule) {
      case 'vente':
        // Interface spécifique pour les COMMERCIAUX uniquement
        if (userRole === 'commercial') {
          return [
            {
              path: '/dashboard-commercial',
              icon: LayoutDashboard,
              label: 'Dashboard',
              badge: null
            },
            {
              path: '/prospects-entrants',
              icon: UserPlus,
              label: 'Prospects entrants',
              badge: null
            },
            {
              path: '/performance',
              icon: TrendingUp,
              label: 'Statistiques',
              badge: null
            },
            {
              path: '/challenges',
              icon: Trophy,
              label: 'Challenges',
              badge: 'hot'
            },
            {
              path: '/chat',
              icon: MessageCircle,
              label: 'Chat',
              badge: null
            },
            {
              path: '/bloc-notes',
              icon: StickyNote,
              label: 'Bloc-notes',
              badge: null
            },
            {
              path: '/boite-a-idees',
              icon: Lightbulb,
              label: 'Boîte à idées',
              badge: null
            },
            {
              path: '/store',
              icon: ShoppingCart,
              label: 'Store',
              badge: 'new'
            },
            {
              path: '/reglages',
              icon: Cog,
              label: 'Réglages',
              badge: null
            }
          ];
        }
        
        // Interface ADMIN du module Vente : menu réorganisé (liste demandée)
        if (userRole === 'admin') {
          return [
            {
              path: '/',
              icon: LayoutDashboard,
              label: 'Dashboard',
              badge: null
            },
            {
              path: '/admin/prospects',
              icon: UserPlus,
              label: 'Leads',
              badge: null,
              adminOnly: true
            },
            {
              path: '/commerciaux',
              icon: User,
              label: 'Team',
              badge: null
            },
            {
              path: '/challenges',
              icon: Trophy,
              label: 'Challenges',
              badge: null
            },
            {
              path: '/chat',
              icon: MessageCircle,
              label: 'Chat',
              badge: null
            },
            {
              path: '/boite-a-idees',
              icon: Lightbulb,
              label: 'Inspiration',
              badge: null
            },
            {
              path: '/bloc-notes',
              icon: StickyNote,
              label: 'Note',
              badge: null
            },
            {
              path: '/admin/pointage',
              icon: Clock,
              label: 'Pointage',
              badge: null,
              adminOnly: true
            },
            {
              path: '/reglages',
              icon: Cog,
              label: 'Paramètres',
              badge: null
            }
          ];
        }

        // Autres rôles non-commerciaux (manager_vente, etc.) - inchangé
        return [
          ...orderedCommonItems
        ];
      case 'marketing':
        return [
          {
            path: '/marketing',
            icon: LayoutDashboard,
            label: 'Dashboard',
            badge: null
          },
          {
            path: '/marketing/campagnes',
            icon: TrendingUp,
            label: 'Campagnes',
            badge: null
          },
          {
            path: '/marketing/landing-pages',
            icon: FileText,
            label: 'Site Web & Analytics',
            badge: null
          },
          {
            path: '/marketing/sources',
            icon: UserPlus,
            label: 'Leads & Performances',
            badge: null
          },
          {
            path: '/chat',
            icon: MessageCircle,
            label: 'Chat',
            badge: null
          },
          {
            path: '/boite-a-idees',
            icon: Lightbulb,
            label: 'Inspiration',
            badge: null
          },
          {
            path: '/bloc-notes',
            icon: StickyNote,
            label: 'Note',
            badge: null
          },
          {
            path: '/reglages',
            icon: Cog,
            label: 'Paramètres',
            badge: null
          }
        ];

      case 'logistique':
        // Interface TECHNICIEN (mobile / tablette)
        if (userRole === 'technicien') {
          return [
            { path: '/ops/tech', icon: LayoutDashboard, label: 'Dashboard', badge: null },
            { path: '/ops/mon-planning', icon: Calendar, label: 'Agenda', badge: null },
            { path: '/ops/mes-chantiers', icon: Truck, label: 'Mes chantiers', badge: null },
            { path: '/ops/chargement', icon: Package, label: 'Chargement', badge: 'soon', desactive: true },
            { path: '/ops/ma-performance', icon: TrendingUp, label: 'Ma performance', badge: 'soon', desactive: true },
            { path: '/chat', icon: MessageCircle, label: 'Chat', badge: 'soon', desactive: true },
            { path: '/bloc-notes', icon: StickyNote, label: 'Note', badge: 'soon', desactive: true },
            { path: '/reglages', icon: Cog, label: 'Paramètres', badge: 'soon', desactive: true }
          ];
        }

        // Interface ADMIN du module Opérations
        return [
          { path: '/ops', icon: LayoutDashboard, label: 'Dashboard', badge: null },
          { path: '/ops/planning', icon: Calendar, label: 'Planning', badge: null },
          { path: '/ops/chantiers', icon: Truck, label: 'Chantiers', badge: null },
          { path: '/ops/sav', icon: Zap, label: 'SAV', badge: null },
          { path: '/ops/equipes', icon: Users, label: 'Équipes', badge: null },
          { path: '/ops/stock', icon: Package, label: 'Stock', badge: null },
          { path: '/chat', icon: MessageCircle, label: 'Chat', badge: null },
          { path: '/boite-a-idees', icon: Lightbulb, label: 'Inspiration', badge: null },
          { path: '/bloc-notes', icon: StickyNote, label: 'Note', badge: null },
          { path: '/reglages', icon: Cog, label: 'Paramètres', badge: null }
        ];

      case 'rh':
        return [
          { path: '/employes', icon: Users, label: 'Employés', badge: null },
          { path: '/chat', icon: MessageCircle, label: 'Chat', badge: null },
          { path: '/boite-a-idees', icon: Lightbulb, label: 'Inspiration', badge: null },
          { path: '/bloc-notes', icon: StickyNote, label: 'Note', badge: null },
          { path: '/reglages', icon: Cog, label: 'Paramètres', badge: null }
        ];

      default:
        return [
          ...orderedCommonItems,
          {
            path: '/users',
            icon: UserCheck,
            label: 'Équipes',
            badge: null,
            adminOnly: true
          },
          ...(userRole === 'admin' ? [{
            path: '/admin/pointage',
            icon: Clock,
            label: 'Gestion Pointage',
            badge: 'admin',
            adminOnly: true
          }, {
            path: '/admin/prospects',
            icon: UserPlus,
            label: 'Prospects Entrants',
            badge: null,
            adminOnly: true
          }, {
            path: '/admin/audit',
            icon: Shield,
            label: 'Système d\'Audit',
            badge: 'admin',
            adminOnly: true
          }, {
            path: '/admin/gestion-pdf',
            icon: FileText,
            label: 'Gestion PDF',
            badge: 'admin',
            adminOnly: true
          }] : [])
        ];
    }
  };

  const menuItems = getMenuItems();

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  // Vérifier si un onglet doit être grisé pour un commercial
  const isDisabledForCommercial = (itemPath) => {
    // Liste des chemins à griser pour les commerciaux
    const disabledPaths = [
      '/performance',      // Statistiques
      '/challenges',       // Challenges
      '/chat',             // Chat
      '/bloc-notes',       // Bloc-notes
      '/boite-a-idees',    // Boîte à idées
      '/store',            // Store
      '/reglages'          // Réglages
    ];

    // Si l'utilisateur est un commercial
    if (userRole === 'commercial') {
      // Exception pour commercial@solaire.fr
      if (user?.email === 'commercial@solaire.fr') {
        return false; // Pas de restrictions
      }
      // Vérifier si le chemin est dans la liste des onglets à griser
      return disabledPaths.includes(itemPath);
    }

    // Onglets pas encore construits pour les techniciens
    if (userRole === 'technicien') {
      return ['/ops/chargement', '/ops/ma-performance', '/chat', '/bloc-notes', '/reglages'].includes(itemPath);
    }

    return false; // Pas de restrictions pour les autres rôles (admin, etc.)
  };

  const getModuleTheme = () => {
    switch(displayModule) {
      case 'vente':
        return {
          gradient: 'from-blue-500 to-cyan-500',
          bg: 'bg-blue-50',
          text: 'text-blue-700',
          border: 'border-blue-200',
          icon: ShoppingCart,
          name: 'Vente'
        };
      case 'marketing':
        return {
          gradient: 'from-pink-500 to-rose-500',
          bg: 'bg-pink-50',
          text: 'text-pink-700',
          border: 'border-pink-200',
          icon: TrendingUp,
          name: 'Marketing'
        };
      case 'logistique':
        return {
          gradient: 'from-green-500 to-emerald-500',
          bg: 'bg-green-50',
          text: 'text-green-700',
          border: 'border-green-200',
          icon: Truck,
          name: 'Opérations'
        };
      case 'rh':
        return {
          gradient: 'from-purple-500 to-pink-500',
          bg: 'bg-purple-50',
          text: 'text-purple-700',
          border: 'border-purple-200',
          icon: Users,
          name: 'RH'
        };
      default:
        return {
          gradient: 'from-primary to-primary-dark',
          bg: 'bg-primary/10',
          text: 'text-primary',
          border: 'border-primary/20',
          icon: Sun,
          name: 'Général'
        };
    }
  };

  const theme = getModuleTheme();
  const ModuleIcon = theme.icon;

  return (
    <>
      {/* Overlay pour mobile */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => onMobileMenuToggle && onMobileMenuToggle(false)}
        />
      )}
      
      {/* Sidebar */}
      <div className={`fixed lg:relative left-0 top-0 z-50 flex flex-col transition-all duration-300 ${
        isCollapsed ? 'w-16' : ''
      } ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}
        style={{
          background: '#FFFFFF',
          border: '0.5px solid rgba(227,232,238,1)',
          width: isCollapsed ? undefined : '220px',
          borderRadius: '20px',
          padding: '18px 14px',
          alignSelf: 'stretch',
          boxShadow: '0 4px 24px rgba(50,50,93,0.08), 0 1px 4px rgba(0,0,0,0.03)',
          overflow: 'hidden'
        }}>
        

        {/* Logo Section - style Pilot (texte ECO ENR) */}
        <div style={{ padding: '10px 10px 4px' }}>
          {!isCollapsed ? (
            <div>
              <div style={{ fontSize: '18px', color: '#1A1F36', fontWeight: 500, lineHeight: 1, fontFamily: 'Georgia, serif' }}>ECO ENR</div>
              <div style={{ fontSize: '8.5px', color: '#697386', letterSpacing: '1.2px', marginTop: '3px', fontWeight: 500 }}>
                CRM EDITION · {getModuleDisplayName(displayModule)}
              </div>
            </div>
          ) : (
            <div style={{ fontSize: '15px', color: '#1A1F36', fontWeight: 500, textAlign: 'center', fontFamily: 'Georgia, serif' }}>E</div>
          )}
        </div>

        {/* Module Switch Intégré - Pour les admins uniquement */}
        {user?.role === 'admin' && !isCollapsed && (
          <div className="mx-4 mt-4 transition-opacity duration-300">
            <ModuleSwitch 
              currentModule={currentModule}
              onModuleChange={onModuleChange}
              isAdmin={user?.role === 'admin'}
              integratedMode={true}
              theme={theme}
            />
          </div>
        )}

        {/* Navigation avec scroll vertical */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Navigation scrollable avec scrollbar personnalisée */}
          <nav className="px-2 flex-1 overflow-y-auto scrollbar-custom" style={{ display: 'flex', flexDirection: 'column', gap: '2px', marginTop: '8px' }}>
            {menuItems
              .filter((item) => !item.adminOnly || userRole === 'admin')
              .map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                const isDisabled = isDisabledForCommercial(item.path);
                
                // Si l'onglet est désactivé, on utilise un div au lieu de NavLink
                if (isDisabled) {
                  return (
                    <div
                      key={item.path}
                      className={`group relative opacity-50 cursor-not-allowed ${isCollapsed ? 'justify-center' : ''}`}
                      style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 10px', borderRadius: '9px', fontSize: '12px' }}
                      title={isCollapsed ? `${item.label} (bientôt disponible)` : 'Bientôt disponible'}
                    >
                      <Icon className="flex-shrink-0" style={{ width: '13px', height: '13px', color: '#B4B2A9' }} />
                      {!isCollapsed && (
                        <>
                          <span className="flex-1" style={{ fontSize: '12px', color: '#B4B2A9' }}>{item.label}</span>
                          {item.badge && (
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium transition-opacity duration-300 ${
                              item.badge === 'new' ? 'bg-gray-300 text-gray-500' :
                              item.badge === 'alert' ? 'bg-gray-300 text-gray-500' :
                              item.badge === 'admin' ? 'bg-gray-300 text-gray-500' :
                              'bg-gray-300 text-gray-500'
                            }`}>
                              {item.badge}
                            </span>
                          )}
                        </>
                      )}
                      {/* Badge en mode collapsed */}
                      {isCollapsed && item.badge && (
                        <div className="absolute -top-1 -right-1 w-3 h-3 bg-gray-300 rounded-full"></div>
                      )}
                    </div>
                  );
                }
                
                return (
                  <NavLink
                    onClick={() => onMobileMenuToggle && onMobileMenuToggle(false)}
                    key={item.path}
                    to={item.path}
                    className={`group relative transition-all duration-150 ${isCollapsed ? 'justify-center' : ''}`}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '10px',
                      padding: '8px 10px', borderRadius: '9px', fontSize: '12px',
                      fontWeight: active ? 500 : 400,
                      color: active ? '#0E7A99' : '#4A5568',
                      background: active ? 'linear-gradient(135deg, rgba(31,209,253,0.12), rgba(255,255,255,0.4))' : 'transparent'
                    }}
                    title={isCollapsed ? item.label : ''}
                  >
                    <Icon className="flex-shrink-0" style={{ width: '13px', height: '13px', color: active ? '#0E7A99' : '#697386' }} />
                    {!isCollapsed && (
                      <>
                        <span className="flex-1" style={{ fontSize: '12px' }}>{item.label}</span>
                        {item.badge && (
                          <span style={{
                            fontSize: '10px', padding: '1px 7px', borderRadius: '100px', fontWeight: 500,
                            background: item.badge === 'alert' ? '#F59E0B' : item.badge === 'admin' ? '#7C5CFC' : item.badge === 'hot' ? '#EF4444' : '#1FD1FD',
                            color: item.badge === 'new' ? '#04344A' : '#FFFFFF'
                          }}>
                            {item.badge}
                          </span>
                        )}
                      </>
                    )}
                    {/* Badge en mode collapsed */}
                    {isCollapsed && item.badge && (
                      <div className="absolute -top-1 -right-1 w-3 h-3 rounded-full" style={{ background: '#1FD1FD' }}></div>
                    )}
                  </NavLink>
                );
              })}
          </nav>
        </div>

        {/* Quick Stats in Sidebar - cachées en mode collapsed */}

      </div>
    </>
  );
};

export default Sidebar;